import { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Checkbox,
  TextField,
  Autocomplete,
  FormControlLabel,
} from "@mui/material";

import "components/pages/transmissions/modal/physician/PhysicianModal.css";

import {
  toggleModal,
  setSelectedRow,
  toggleProcessing,
} from "app/store/transmission/slice/transmissionSlice";

import {
  useGetPhysiciansQuery,
  useAssignPhysicianToReportMutation,
} from "app/store/transmission/api/transmissionTableApi";
import { showErrorToast, showSuccessToast } from "utils/toast";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import { isValidArray } from "utils/array";

const PhysicianModal = () => {
  const { isPhysicianModal, isProcessing, selectedRow } = useSelector(
    (state) => state.transmission
  );

  const [formState, setFormState] = useState({
    selectedPhysician: selectedRow?.assignedUser?.user_id
      ? {
          name: selectedRow?.assignedUser?.name,
          userId: selectedRow?.assignedUser?.user_id,
        }
      : null,
    assignToPatient: false,
  });

  const dispatch = useDispatch();
  const { data: physicianData } = useGetPhysiciansQuery(
    { isPhysicianModal, active: true },
    { refetchOnMountOrArgChange: true }
  );

  const physicianOptions = useMemo(() => {
    if (isValidArray(physicianData)) {
      return physicianData.filter((physician) =>
        physician.accounts.some((account) =>
          account.includes(selectedRow?.account)
        )
      );
    }

    return [];
  }, [physicianData, selectedRow?.account]);

  const selectedPhysician =
    physicianOptions.find(
      (physician) => physician.userId === formState.selectedPhysician?.userId
    ) || null;

  const [assignPhysicianToReport] = useAssignPhysicianToReportMutation();

  const onConfirm = async () => {
    try {
      dispatch(toggleProcessing(true));
      const response = await assignPhysicianToReport({
        reportId: selectedRow?._id,
        physicianId: selectedPhysician?.userId,
        assignToPatient: formState.assignToPatient,
      }).unwrap();

      if (!response?.error) {
        showSuccessToast(response?.message);
      }
    } catch (error) {
      showErrorToast(error?.message);
      dispatch(toggleModal({ type: "physician", value: true }));
    } finally {
      dispatch(toggleProcessing(false));
      dispatch(toggleModal({ type: "physician", value: false }));
      dispatch(setSelectedRow(null));
    }
  };

  const onClose = () => {
    dispatch(toggleProcessing(false));
    dispatch(toggleModal({ type: "physician", value: false }));
    setFormState({
      selectedPhysician: null,
      assignToPatient: false,
    });
    dispatch(setSelectedRow(null));
  };

  return (
    <ModalWrapper
      show={isPhysicianModal}
      onClose={onClose}
      fullWidth
      title="Assign Physician to this Report"
      actionButtons={[
        {
          label: "Cancel",
          variant: "outlined",
          color: "secondary",
          onClick: onClose,
          disabled: isProcessing,
        },
        {
          label: "Save",
          variant: "contained",
          color: "secondary",
          onClick: onConfirm,
          disabled: isProcessing || !selectedPhysician,
          isLoading: isProcessing,
        },
      ]}
    >
      <div className="mt-2">
        <Autocomplete
          id="physician"
          options={physicianOptions || []}
          value={selectedPhysician}
          onChange={(_event, selectedOption) => {
            setFormState((prevState) => ({
              ...prevState,
              selectedPhysician: selectedOption,
            }));
          }}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) =>
            option?.userId === value?.userId
          }
          renderInput={(params) => (
            <TextField {...params} label="Physician" variant="outlined" />
          )}
        />

        {selectedPhysician && (
          <FormControlLabel
            className="mt-2"
            control={
              <Checkbox
                checked={formState.assignToPatient}
                onChange={(e) =>
                  setFormState((prevState) => ({
                    ...prevState,
                    assignToPatient: e.target.checked,
                  }))
                }
                color="primary"
              />
            }
            label={
              <>
                Assign <strong>{selectedPhysician?.name}</strong> as the
                following physician for patient{" "}
                <strong>{selectedRow?.name}</strong>
              </>
            }
          />
        )}
      </div>
    </ModalWrapper>
  );
};

export default PhysicianModal;
